/**
 * Clustering service — HTTP + SSE client.
 * Routed through the gateway at /api/v1/clustering/* (stripped to /v1/* upstream).
 */

import { API_BASE, authenticatedFetch } from './authenticatedFetch';
import type {
  BatchConfig,
  BatchCreate,
  BatchRead,
  BatchSummaryRead,
  BatchesListParams,
  ClusterRead,
  ClusterRename,
  ClustersListParams,
  EstimateRequest,
  EstimateResult,
  ExportFormat,
  KeywordRead,
  KeywordsListParams,
  ProgressEvent,
  ProjectCreate,
  ProjectRead,
  ProjectUpdate,
  ReclusterRequest,
} from '../types/clustering';

const BASE = '/api/v1/clustering';
const SERVICE = 'Clustering';

function omitCreateManagedConfigFields(
  config?: Partial<BatchConfig>,
): Partial<BatchConfig> | undefined {
  if (!config) return config;
  const {
    algorithm: _algorithm,
    resolution: _resolution,
    fetch_keyword_difficulty: _fetchKeywordDifficulty,
    ...rest
  } = config;
  return rest;
}

function normalizeCreatePayload<T extends { config?: Partial<BatchConfig> }>(body: T): T {
  if (!body.config) return body;
  return {
    ...body,
    config: omitCreateManagedConfigFields(body.config),
  };
}

function omitManagedReclusterFields(body: ReclusterRequest): ReclusterRequest {
  const {
    algorithm: _algorithm,
    resolution: _resolution,
    ...rest
  } = body as ReclusterRequest & {
    algorithm?: unknown;
    resolution?: unknown;
  };
  return rest;
}

function qs(params: object | undefined): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// --- Projects --------------------------------------------------------------

export const listProjects = () =>
  authenticatedFetch<ProjectRead[]>(`${BASE}/projects`, { serviceName: SERVICE });

export const getProject = (id: string) =>
  authenticatedFetch<ProjectRead>(`${BASE}/projects/${id}`, { serviceName: SERVICE });

export const createProject = (body: ProjectCreate) =>
  authenticatedFetch<ProjectRead>(`${BASE}/projects`, {
    serviceName: SERVICE,
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateProject = (id: string, body: ProjectUpdate) =>
  authenticatedFetch<ProjectRead>(`${BASE}/projects/${id}`, {
    serviceName: SERVICE,
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const deleteProject = (id: string) =>
  authenticatedFetch<void>(`${BASE}/projects/${id}`, {
    serviceName: SERVICE,
    method: 'DELETE',
  });

// --- Estimate + batches ----------------------------------------------------

export const estimateBatch = (body: EstimateRequest) =>
  authenticatedFetch<EstimateResult>(`${BASE}/estimate`, {
    serviceName: SERVICE,
    method: 'POST',
    body: JSON.stringify(normalizeCreatePayload(body)),
  });

export const createBatch = (body: BatchCreate) =>
  authenticatedFetch<BatchRead>(`${BASE}/batches`, {
    serviceName: SERVICE,
    method: 'POST',
    body: JSON.stringify(normalizeCreatePayload(body)),
  });

export const listBatches = (params?: BatchesListParams) =>
  authenticatedFetch<BatchRead[]>(`${BASE}/batches${qs(params)}`, { serviceName: SERVICE });

export const getBatch = (id: string) =>
  authenticatedFetch<BatchRead>(`${BASE}/batches/${id}`, { serviceName: SERVICE });

export const getBatchSummary = (id: string) =>
  authenticatedFetch<BatchSummaryRead>(`${BASE}/batches/${id}/summary`, { serviceName: SERVICE });

export const regenerateBatchSummary = (id: string) =>
  authenticatedFetch<BatchSummaryRead>(`${BASE}/batches/${id}/summary/regenerate`, {
    serviceName: SERVICE,
    method: 'POST',
  });

export const rerunBatch = (id: string) =>
  authenticatedFetch<BatchRead>(`${BASE}/batches/${id}/rerun`, {
    serviceName: SERVICE,
    method: 'POST',
  });

export const reclusterBatch = (id: string, body: ReclusterRequest = {}) =>
  authenticatedFetch<BatchRead>(`${BASE}/batches/${id}/recluster`, {
    serviceName: SERVICE,
    method: 'POST',
    body: JSON.stringify(omitManagedReclusterFields(body)),
  });

export const cancelBatch = (id: string) =>
  authenticatedFetch<BatchRead>(`${BASE}/batches/${id}/cancel`, {
    serviceName: SERVICE,
    method: 'POST',
  });

export const deleteBatch = (id: string) =>
  authenticatedFetch<void>(`${BASE}/batches/${id}`, {
    serviceName: SERVICE,
    method: 'DELETE',
  });

// --- Clusters + keywords ---------------------------------------------------

export const listClusters = (batchId: string, params?: ClustersListParams) =>
  authenticatedFetch<ClusterRead[]>(
    `${BASE}/batches/${batchId}/clusters${qs(params)}`,
    { serviceName: SERVICE },
  );

export const listKeywords = (batchId: string, params?: KeywordsListParams) =>
  authenticatedFetch<KeywordRead[]>(
    `${BASE}/batches/${batchId}/keywords${qs(params)}`,
    { serviceName: SERVICE },
  );

export const renameCluster = (clusterId: string, body: ClusterRename) =>
  authenticatedFetch<ClusterRead>(`${BASE}/clusters/${clusterId}`, {
    serviceName: SERVICE,
    method: 'PATCH',
    body: JSON.stringify(body),
  });

// --- Export ----------------------------------------------------------------

export async function downloadBatchExport(
  batchId: string,
  format: ExportFormat,
): Promise<Blob> {
  const token = localStorage.getItem('access_token');
  const res = await fetch(`${API_BASE}${BASE}/batches/${batchId}/export?format=${format}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const err = new Error(`Export failed (${res.status})`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  return res.blob();
}


// --- SSE progress ----------------------------------------------------------

export interface ProgressSubscription {
  /** Stop receiving events and release the underlying connection. */
  close: () => void;
}

export interface ProgressSubscribeOptions {
  onEvent: (event: ProgressEvent) => void;
  onError?: (error: Error) => void;
  /** Called once the server sends `kind: "complete"`. */
  onComplete?: (event: ProgressEvent) => void;
  /** Poll interval (ms) used when SSE is unavailable. Default 3000. */
  fallbackPollMs?: number;
}

/**
 * Subscribe to live batch progress.
 *
 * Uses fetch + ReadableStream so we can pass the Bearer token in the
 * Authorization header (EventSource cannot). If the server does not respond
 * with `text/event-stream` (e.g., a proxy that buffers responses) we fall
 * back to polling `GET /batches/{id}` until a terminal status is reached.
 */
export function subscribeBatchProgress(
  batchId: string,
  opts: ProgressSubscribeOptions,
): ProgressSubscription {
  const ac = new AbortController();
  let closed = false;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;

  const close = () => {
    if (closed) return;
    closed = true;
    ac.abort();
    if (pollTimer) clearTimeout(pollTimer);
  };

  const terminal = (e: ProgressEvent) => {
    opts.onEvent(e);
    if (e.kind === 'complete') {
      opts.onComplete?.(e);
      close();
    }
  };

  const startPolling = () => {
    const interval = opts.fallbackPollMs ?? 3000;
    const tick = async () => {
      if (closed) return;
      try {
        const b = await getBatch(batchId);
        const terminalStatus =
          b.status === 'done' || b.status === 'failed' || b.status === 'cancelled';
        terminal({
          batch_id: b.id,
          status: b.status,
          current_step: b.current_step,
          progress_pct: b.progress_pct,
          serp_cache_hits: b.serp_cache_hits,
          serp_cache_misses: b.serp_cache_misses,
          clusters_count: b.clusters_count,
          ts: b.updated_at,
          kind: terminalStatus ? 'complete' : 'progress',
        });
      } catch (err) {
        opts.onError?.(err as Error);
      }
      if (!closed) pollTimer = setTimeout(tick, interval);
    };
    void tick();
  };

  (async () => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(
        `${API_BASE}${BASE}/batches/${batchId}/stream`,
        {
          headers: {
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: ac.signal,
          cache: 'no-store',
        },
      );
      if (closed) return;
      const ct = res.headers.get('content-type') || '';
      if (!res.ok || !ct.includes('text/event-stream') || !res.body) {
        startPolling();
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const data = frame
            .split('\n')
            .filter((l) => l.startsWith('data:'))
            .map((l) => l.slice(5).trim())
            .join('\n');
          if (!data) continue;
          try {
            terminal(JSON.parse(data) as ProgressEvent);
          } catch {
            // ignore malformed frame
          }
        }
      }
    } catch (err) {
      if (closed || (err as { name?: string }).name === 'AbortError') return;
      opts.onError?.(err as Error);
      startPolling();
    }
  })();

  return { close };
}
